// src/ApiPanel/pages/ComplaintCenter.jsx
// Complaint — the ticket system that already exists behind
// POST /ticket_manager_action.
//
// The server scopes the list by role (a Retailer sees their own, a
// Distributor sees their members', Admin and above see everything) and its
// own _can() table decides who may edit, delete, assign and post internal
// notes. This screen mirrors that table so nobody is shown a button that
// will come back 403 — but the server is what enforces it, and it re-checks
// on every action regardless of what is rendered here.
//
// One API quirk worth knowing: this endpoint answers {"status": "success"}
// and {"status": "error"} as strings, unlike the rest of the API which uses
// a boolean. apiPanelFetch handles both, so a failure here surfaces as a
// thrown message rather than a silently empty list.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge, Card, DataTable, Modal, Notice, ReportPage, SelectField, Stat,
  StatGrid, TextField, count, rupees,
} from "../components/reports/ReportKit";
import { FilterBar } from "../components/reports/ReportKit";
import { useAction } from "../hooks/useReportQuery";
import { apiPanelFetch } from "../utils/apiPanelFetch";
import { API_PANEL_ENDPOINTS } from "../constants/apiPanelAuth";
import { useApiPanelSession } from "../context/ApiPanelSessionContext";

const TYPES = [
  { value: "support", label: "General Support" },
  { value: "payment", label: "Payment Issue" },
  { value: "kyc", label: "KYC / Compliance" },
  { value: "account", label: "Account Issue" },
  { value: "fraud", label: "Fraud Alert" },
  { value: "bug", label: "Bug" },
  { value: "feature", label: "Feature Request" },
];

const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

const STATUSES = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "pending", label: "Pending" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

// The server's _can() table, keyed by its own snake_case role names.
const PERMS = {
  main_admin: { create: 1, edit: 1, delete: 1, assign: 1 },
  admin: { create: 1, edit: 1, delete: 1, assign: 1 },
  white_label: { create: 1, edit: 1, delete: 0, assign: 1 },
  super_distributor: { create: 1, edit: 1, delete: 0, assign: 0 },
  distributor: { create: 1, edit: 0, delete: 0, assign: 0 },
  retailer: { create: 1, edit: 0, delete: 0, assign: 0 },
};

const roleKey = (role) =>
  String(role || "").trim().toLowerCase().replace(/\s+/g, "_")
    .replace("distributer", "distributor");

const can = (role, what) => !!PERMS[roleKey(role)]?.[what];

const priorityTone = (p) =>
  /urgent|high/i.test(p) ? "bad" : /medium/i.test(p) ? "warn" : "neutral";

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
};

export default function ComplaintCenter() {
  const { user } = useApiPanelSession();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ status: "", priority: "", ticket_type: "", q: "" });
  const [showCreate, setShowCreate] = useState(false);
  const [openTicket, setOpenTicket] = useState(null);
  const action = useAction();

  const mayCreate = can(user?.role, "create");
  const mayEdit = can(user?.role, "edit");
  const mayDelete = can(user?.role, "delete");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiPanelFetch(API_PANEL_ENDPOINTS.TICKET_ACTION, {
        method: "POST",
        form: { action: "GET" },
      });
      setTickets(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setError(err.message || "Could not load complaints.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    return tickets.filter((t) => {
      if (filters.status && String(t.status) !== filters.status) return false;
      if (filters.priority && String(t.priority) !== filters.priority) return false;
      if (filters.ticket_type && String(t.ticket_type) !== filters.ticket_type) return false;
      if (!q) return true;
      return [t.ticket_id, t.title, t.description, t.reference_number, t.created_by_name, t.category]
        .some((v) => String(v ?? "").toLowerCase().includes(q));
    });
  }, [tickets, filters]);

  const stats = useMemo(() => {
    const s = { open: 0, progress: 0, resolved: 0, urgent: 0 };
    tickets.forEach((t) => {
      const st = String(t.status ?? "").toLowerCase();
      if (/resolved|closed/.test(st)) s.resolved += 1;
      else if (/progress/.test(st)) s.progress += 1;
      else s.open += 1;
      if (/urgent|high/i.test(String(t.priority ?? ""))) s.urgent += 1;
    });
    return s;
  }, [tickets]);

  const setStatus = async (ticket, status) => {
    const res = await action.run(
      API_PANEL_ENDPOINTS.TICKET_ACTION,
      { action: "UPDATE_STATUS", ticket_id: ticket.id, status },
      { form: true }
    );
    if (res) {
      setOpenTicket((t) => (t && t.id === ticket.id ? { ...t, status } : t));
      load();
    }
  };

  const remove = async (ticket) => {
    if (!window.confirm(
      `Delete complaint ${ticket.ticket_id}?\n\nThis removes it and its comments permanently.`
    )) return;
    const res = await action.run(
      API_PANEL_ENDPOINTS.TICKET_ACTION,
      { action: "DELETE", id: ticket.id },
      { form: true }
    );
    if (res) {
      setOpenTicket(null);
      load();
    }
  };

  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <ReportPage
      title="Complaint"
      subtitle="Support tickets raised against transactions, KYC, payments and account issues."
      actions={
        mayCreate ? (
          <button type="button" className="apip-btn apip-btn-primary" onClick={() => setShowCreate(true)}>
            <i className="iconoir-plus" /> Raise Complaint
          </button>
        ) : null
      }
    >
      {action.message && <Notice tone="ok">{action.message}</Notice>}
      {action.error && <Notice tone="bad">{action.error}</Notice>}

      <StatGrid>
        <Stat label="Open" value={count(stats.open)} tone={stats.open ? "warn" : ""} />
        <Stat label="In progress" value={count(stats.progress)} />
        <Stat label="Resolved" value={count(stats.resolved)} tone="ok" />
        <Stat label="High / urgent" value={count(stats.urgent)} tone={stats.urgent ? "warn" : ""} />
        <Stat label="Total" value={count(tickets.length)} tone="brand" />
      </StatGrid>

      <Card>
        <FilterBar
          onApply={load}
          onReset={() => setFilters({ status: "", priority: "", ticket_type: "", q: "" })}
          busy={loading}
        >
          <SelectField label="Status" value={filters.status} onChange={set("status")} options={STATUSES} />
          <SelectField label="Priority" value={filters.priority} onChange={set("priority")} options={PRIORITIES} />
          <SelectField label="Type" value={filters.ticket_type} onChange={set("ticket_type")} options={TYPES} />
          <TextField label="Search" value={filters.q} onChange={set("q")} placeholder="Ticket, title, reference…" />
        </FilterBar>
      </Card>

      <Card title="Complaints">
        <DataTable
          columns={[
            { key: "ticket_id", label: "Ticket", render: (t) => <span className="apip-mono">{t.ticket_id}</span> },
            {
              key: "title",
              label: "Subject",
              render: (t) => (
                <span className="apip-party">
                  {t.title || "—"}
                  {t.category && <small>{t.category}</small>}
                </span>
              ),
            },
            {
              key: "ticket_type",
              label: "Type",
              render: (t) => TYPES.find((x) => x.value === t.ticket_type)?.label || t.ticket_type || "—",
            },
            {
              key: "priority",
              label: "Priority",
              render: (t) => <Badge value={t.priority} tone={priorityTone(t.priority)} />,
            },
            {
              key: "reference_number",
              label: "Reference",
              render: (t) => (t.reference_number ? <span className="apip-mono">{t.reference_number}</span> : "—"),
            },
            {
              key: "amount_involved",
              label: "Amount",
              numeric: true,
              render: (t) => (t.amount_involved ? rupees(t.amount_involved) : "—"),
            },
            { key: "created_by_name", label: "Raised by", render: (t) => t.created_by_name || "—" },
            { key: "assigned_to_name", label: "Assigned", render: (t) => t.assigned_to_name || "—" },
            { key: "created_at", label: "Raised", render: (t) => fmt(t.created_at) },
            { key: "status", label: "Status", render: (t) => <Badge value={t.status} /> },
            {
              key: "actions",
              label: "",
              width: 90,
              render: (t) => (
                <button type="button" className="apip-btn apip-btn-sm apip-btn-ghost" onClick={() => setOpenTicket(t)}>
                  Open
                </button>
              ),
            },
          ]}
          rows={rows}
          loading={loading}
          error={error}
          empty={tickets.length ? "No complaints match these filters." : "No complaints raised yet."}
          rowKey={(t) => t.id}
        />
      </Card>

      <CreateModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onDone={() => {
          setShowCreate(false);
          load();
        }}
      />

      <TicketDetail
        ticket={openTicket}
        onClose={() => setOpenTicket(null)}
        onStatus={setStatus}
        onDelete={remove}
        mayEdit={mayEdit}
        mayDelete={mayDelete}
        mayInternal={can(user?.role, "assign")}
        busy={action.busy}
      />
    </ReportPage>
  );
}

/* ── detail + comment thread ──────────────────────────────────────────── */

function TicketDetail({ ticket, onClose, onStatus, onDelete, mayEdit, mayDelete, mayInternal, busy }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [internal, setInternal] = useState(false);
  const [posting, setPosting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!ticket) return undefined;
    let cancelled = false;
    setLoading(true);
    setComments([]);
    setDraft("");
    setInternal(false);
    setErr("");
    apiPanelFetch(API_PANEL_ENDPOINTS.TICKET_ACTION, {
      method: "POST",
      form: { action: "GET_COMMENTS", ticket_id: ticket.id },
    })
      .then((res) => !cancelled && setComments(Array.isArray(res?.data) ? res.data : []))
      .catch((e) => !cancelled && setErr(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [ticket]);

  const post = async (e) => {
    e.preventDefault();
    if (posting || draft.trim().length < 2) return;
    setPosting(true);
    setErr("");
    try {
      const res = await apiPanelFetch(API_PANEL_ENDPOINTS.TICKET_ACTION, {
        method: "POST",
        form: {
          action: "ADD_COMMENT",
          ticket_id: ticket.id,
          content: draft.trim(),
          is_internal: internal ? "1" : "0",
        },
      });
      if (res?.comment) setComments((c) => [...c, res.comment]);
      setDraft("");
    } catch (e2) {
      setErr(e2.message || "Could not post that comment.");
    } finally {
      setPosting(false);
    }
  };

  if (!ticket) return null;

  return (
    <Modal
      open
      wide
      title={`${ticket.ticket_id} — ${ticket.title || "Complaint"}`}
      onClose={onClose}
      footer={
        <>
          {mayDelete && (
            <button
              type="button"
              className="apip-btn apip-btn-ghost apip-btn-danger"
              disabled={busy}
              onClick={() => onDelete(ticket)}
            >
              Delete
            </button>
          )}
          {mayEdit && !/resolved|closed/i.test(String(ticket.status)) && (
            <button
              type="button"
              className="apip-btn apip-btn-primary"
              disabled={busy}
              onClick={() => onStatus(ticket, "resolved")}
            >
              Mark resolved
            </button>
          )}
        </>
      }
    >
      <dl className="apip-kv apip-kv-2">
        <dt>Status</dt><dd><Badge value={ticket.status} /></dd>
        <dt>Priority</dt><dd><Badge value={ticket.priority} tone={priorityTone(ticket.priority)} /></dd>
        <dt>Type</dt>
        <dd>{TYPES.find((x) => x.value === ticket.ticket_type)?.label || ticket.ticket_type || "—"}</dd>
        <dt>Category</dt><dd>{ticket.category || "—"}</dd>
        <dt>Reference</dt><dd className="apip-mono">{ticket.reference_number || "—"}</dd>
        <dt>Amount</dt><dd>{ticket.amount_involved ? rupees(ticket.amount_involved) : "—"}</dd>
        <dt>Raised by</dt><dd>{ticket.created_by_name || "—"}</dd>
        <dt>Assigned to</dt><dd>{ticket.assigned_to_name || "Unassigned"}</dd>
        <dt>Raised</dt><dd>{fmt(ticket.created_at)}</dd>
        <dt>Due</dt><dd>{ticket.due_date ? fmt(ticket.due_date) : "—"}</dd>
      </dl>

      <div>
        <h4 className="apip-form-subhead">Description</h4>
        <p className="apip-prose">{ticket.description || "—"}</p>
      </div>

      {mayEdit && (
        <div>
          <h4 className="apip-form-subhead">Move to</h4>
          <div className="apip-form-actions">
            {STATUSES.filter((s) => s.value !== ticket.status).map((s) => (
              <button
                key={s.value}
                type="button"
                className="apip-btn apip-btn-sm apip-btn-ghost"
                disabled={busy}
                onClick={() => onStatus(ticket, s.value)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <h4 className="apip-form-subhead">Conversation</h4>
        {loading ? (
          <div className="apip-loading">Loading…</div>
        ) : comments.length === 0 ? (
          <p className="apip-hint">No comments yet.</p>
        ) : (
          <ul className="apip-thread">
            {comments.map((c) => (
              <li key={c.id} className={c.is_internal ? "internal" : ""}>
                <div className="apip-thread-head">
                  <strong>{c.author_name}</strong>
                  {c.is_internal && <span className="apip-badge apip-badge-warn">Internal note</span>}
                  <span className="apip-hint">{fmt(c.created_at)}</span>
                </div>
                <p className="apip-prose">{c.content}</p>
              </li>
            ))}
          </ul>
        )}
      </div>

      <form onSubmit={post}>
        <label className="apip-field">
          <span>Add a comment</span>
          <textarea
            className="apip-input"
            rows={3}
            maxLength={2000}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="What has changed, or what you need from the other side"
          />
        </label>
        <div className="apip-form-actions">
          {mayInternal && (
            <label className="apip-check">
              <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
              <span>Internal note — hidden from the person who raised it</span>
            </label>
          )}
          <button
            type="submit"
            className="apip-btn apip-btn-primary"
            disabled={posting || draft.trim().length < 2}
          >
            {posting ? "Posting…" : "Post"}
          </button>
        </div>
        {err && <Notice tone="bad">{err}</Notice>}
      </form>
    </Modal>
  );
}

/* ── raise a complaint ────────────────────────────────────────────────── */

function CreateModal({ open, onClose, onDone }) {
  const action = useAction();
  const [form, setForm] = useState({
    title: "", description: "", ticket_type: "support", priority: "medium",
    reference_number: "", amount_involved: "", category: "", due_date: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const res = await action.run(
      API_PANEL_ENDPOINTS.TICKET_ACTION,
      { action: "CREATE", status: "open", currency: "INR", ...form },
      { form: true }
    );
    if (res) {
      setForm({
        title: "", description: "", ticket_type: "support", priority: "medium",
        reference_number: "", amount_involved: "", category: "", due_date: "",
      });
      onDone();
    }
  };

  return (
    <Modal
      open={open}
      wide
      title="Raise a complaint"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="apip-btn apip-btn-ghost" onClick={onClose}>Cancel</button>
          <button
            type="submit"
            form="apip-ticket-form"
            className="apip-btn apip-btn-primary"
            disabled={action.busy || form.title.trim().length < 3 || form.description.trim().length < 5}
          >
            {action.busy ? "Submitting…" : "Submit complaint"}
          </button>
        </>
      }
    >
      <form id="apip-ticket-form" onSubmit={submit}>
        <label className="apip-field">
          <span>Subject</span>
          <input type="text" className="apip-input" required maxLength={255} value={form.title}
            onChange={set("title")} placeholder="One line describing the problem" />
        </label>

        <label className="apip-field">
          <span>Description</span>
          <textarea className="apip-input" rows={4} required maxLength={4000} value={form.description}
            onChange={set("description")}
            placeholder="What happened, when, and what you expected instead" />
        </label>

        <div className="apip-form-grid">
          <label className="apip-field">
            <span>Type</span>
            <select className="apip-input" value={form.ticket_type} onChange={set("ticket_type")}>
              {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </label>
          <label className="apip-field">
            <span>Priority</span>
            <select className="apip-input" value={form.priority} onChange={set("priority")}>
              {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </label>
          <label className="apip-field">
            <span>Category</span>
            <input type="text" className="apip-input" maxLength={100} value={form.category}
              onChange={set("category")} placeholder="e.g. Recharge" />
          </label>
          <label className="apip-field">
            <span>Transaction reference</span>
            <input type="text" className="apip-input apip-mono" maxLength={100}
              value={form.reference_number} onChange={set("reference_number")}
              placeholder="Order or txn id" />
          </label>
          <label className="apip-field">
            <span>Amount involved</span>
            <input type="number" className="apip-input" min="0" step="0.01"
              value={form.amount_involved} onChange={set("amount_involved")} placeholder="0.00" />
          </label>
          <label className="apip-field">
            <span>Needed by</span>
            <input type="date" className="apip-input" value={form.due_date} onChange={set("due_date")} />
          </label>
        </div>

        <Notice tone="info">
          Quoting the transaction reference is what lets support find it without
          asking you twice. Do not paste passwords, OTPs or your API key into a
          complaint — nobody who can help you needs them.
        </Notice>

        {action.error && <Notice tone="bad">{action.error}</Notice>}
      </form>
    </Modal>
  );
}
